import { MoreVertical, Plus, Save, Timer, X } from "lucide-react";
import { useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";
import EmptyState from "../../components/EmptyState.jsx";
import { Field } from "../../components/FormControls.jsx";
import Modal from "../../components/Modal.jsx";
import SearchBar from "../../components/SearchBar.jsx";
import { SkeletonList } from "../../components/Skeleton.jsx";
import StatusChip from "../../components/StatusChip.jsx";
import { useAuth } from "../../context/AuthContext.jsx";
import { useToast } from "../../context/ToastContext.jsx";
import { saveRoutineLog, suspendRoutine } from "../../firebase/firestore.js";
import { getNowTimeValue } from "../../utils/date.js";
import { matchesSearch, searchableRoutine } from "../../utils/search.js";
import { SUSPENSION_REASONS, getRoutineLogStatus } from "../../utils/status.js";
import { formatTime, getClassMoment, timeRangeLabel, timeToMinutes } from "../../utils/time.js";

function sortRoutineCards(a, b) {
  const rank = { running: 0, upcoming: 1, past: 2, later: 3 };
  const momentCompare = rank[getClassMoment(a.routine)] - rank[getClassMoment(b.routine)];
  if (momentCompare !== 0) return momentCompare;
  const roomCompare = String(a.routine.roomNumber || "").localeCompare(String(b.routine.roomNumber || ""), undefined, {
    numeric: true
  });
  if (roomCompare !== 0) return roomCompare;
  return Number(timeToMinutes(a.routine.scheduledIn) || 0) - Number(timeToMinutes(b.routine.scheduledIn) || 0);
}

function roomCanBeEntered(roomNumber) {
  const value = String(roomNumber || "").trim().toUpperCase();
  return !value || value === "N/A";
}

function RoutineCard({ routine, log, status, onTimeClick, onSuspend }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const actionLabel = !log?.actualIn ? "Add IN Time" : !log?.actualOut ? "Edit IN Time / Add OUT Time" : "Completed";
  const disabled = Boolean(log?.actualIn && log?.actualOut) || log?.classStatus === "suspended";

  return (
    <article className="routine-card card pressable">
      <div className="routine-card-top">
        <div>
          <p className="eyebrow">Room</p>
          <h2>{routine.roomNumber || "No room"}</h2>
        </div>
        <div className="card-menu-wrap">
          <button className="icon-button" type="button" onClick={() => setMenuOpen((value) => !value)}>
            <MoreVertical size={19} />
          </button>
          {menuOpen ? (
            <div className="card-menu">
              {SUSPENSION_REASONS.map((reason) => (
                <button
                  key={reason}
                  type="button"
                  onClick={() => {
                    setMenuOpen(false);
                    onSuspend(reason);
                  }}
                >
                  {reason}
                </button>
              ))}
            </div>
          ) : null}
        </div>
      </div>
      <div className="routine-main">
        <h3>
          {routine.className || "Class"} - {routine.sectionName || "Section"}
        </h3>
        <p className="subject-line">📘 {routine.subjectName || "Subject"}</p>
        <p>Teacher: {routine.teacherName || "Not assigned"}</p>
        <p>Schedule: {timeRangeLabel(routine.scheduledIn, routine.scheduledOut)}</p>
        {log?.actualIn ? <p>IN: {formatTime(log.actualIn)}</p> : null}
        {log?.actualOut ? <p>OUT: {formatTime(log.actualOut)}</p> : null}
      </div>
      <div className="routine-footer">
        <StatusChip status={status} />
        <button className="btn btn-primary" type="button" disabled={disabled} onClick={onTimeClick}>
          <Timer size={17} />
          {actionLabel}
        </button>
      </div>
    </article>
  );
}

export default function UserHome() {
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);
  const [timeModal, setTimeModal] = useState(null);
  const { user } = useAuth();
  const { showToast } = useToast();
  const { date, day, routines, logs, settings, loading, routineMasters } = useOutletContext();

  const logByRoutine = useMemo(() => {
    return Object.fromEntries(logs.map((log) => [log.routineId, log]));
  }, [logs]);

  const visibleCards = useMemo(() => {
    const hasSearch = search.trim().length > 0;
    return routines
      .map((routine) => {
        const log = logByRoutine[routine.id] || logByRoutine[routine.routineId];
        const status = getRoutineLogStatus(routine, log);
        return { routine, log, status };
      })
      .filter((item) => {
        const isCompleted = item.log?.actualIn && item.log?.actualOut;
        const isPendingOut = item.log?.actualIn && !item.log?.actualOut;
        const isSuspended = item.log?.classStatus === "suspended";
        if (!hasSearch && (isCompleted || isPendingOut || isSuspended)) return false;
        return matchesSearch(searchableRoutine(item.routine, item.log, item.status), search, [
          "roomNumber",
          "teacherName",
          "className",
          "sectionName",
          "subjectName",
          "scheduledIn",
          "scheduledOut",
          "day",
          "status",
          "actualIn",
          "actualOut"
        ]);
      })
      .sort(sortRoutineCards);
  }, [routines, logByRoutine, search]);

  function openTimeModal(routine, log) {
    setTimeModal({
      routine,
      log,
      actualIn: log?.actualIn || getNowTimeValue(),
      actualOut: log?.actualOut || getNowTimeValue(),
      roomNumber: roomCanBeEntered(log?.roomNumber || routine.roomNumber) ? "" : log?.roomNumber || routine.roomNumber || "",
      teacherName: log?.teacherName || routine.teacherName || "",
      includeOut: Boolean(log?.actualIn && !log?.actualOut),
      mode: log?.actualIn && !log?.actualOut ? "out" : "in"
    });
  }

  async function handleSaveTime(event) {
    event.preventDefault();
    if (!timeModal) return;
    setSaving(true);
    try {
      const roomPatch = roomCanBeEntered(timeModal.routine?.roomNumber) && timeModal.roomNumber?.trim()
        ? { roomNumber: timeModal.roomNumber.trim() }
        : {};
      const snapshotPatch = {
        ...(timeModal.teacherName ? { teacherName: timeModal.teacherName } : {}),
        ...roomPatch
      };
      const patch =
        timeModal.mode === "in"
          ? {
              actualIn: timeModal.actualIn,
              ...(timeModal.includeOut ? { actualOut: timeModal.actualOut } : {}),
              ...snapshotPatch,
              classStatus: "normal",
              suspensionReason: ""
            }
          : {
              actualIn: timeModal.actualIn,
              actualOut: timeModal.actualOut,
              ...snapshotPatch,
              classStatus: "normal",
              suspensionReason: ""
            };
      await saveRoutineLog({
        routine: timeModal.routine,
        date,
        day,
        patch,
        user,
        settings
      });
      showToast(timeModal.mode === "in" ? "IN time saved" : "OUT time saved");
      setTimeModal(null);
    } catch (error) {
      showToast(error.message || "Unable to save time", "error");
    } finally {
      setSaving(false);
    }
  }

  async function handleSuspend(routine, reason) {
    setSaving(true);
    try {
      await suspendRoutine({ routine, date, day, reason, user, settings });
      showToast("Class marked as suspended", "warning");
    } catch (error) {
      showToast(error.message || "Unable to suspend class", "error");
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <section className="page-hero compact">
        <p>{day}, {date}</p>
        <h2>Today’s Classes</h2>
      </section>
      <SearchBar
        value={search}
        onChange={setSearch}
        placeholder="Search room, teacher, class, section, subject, time, day or status"
      />
      {loading ? (
        <SkeletonList />
      ) : visibleCards.length ? (
        <div className="routine-list">
          {visibleCards.map(({ routine, log, status }) => (
            <RoutineCard
              key={routine.id}
              routine={routine}
              log={log}
              status={status}
              onTimeClick={() => openTimeModal(routine, log)}
              onSuspend={(reason) => handleSuspend(routine, reason)}
            />
          ))}
        </div>
      ) : (
        <EmptyState
          icon="✅"
          title={search ? "No matching class" : "All clear for now"}
          text={search ? "Try another room, teacher, subject or status." : "Completed and pending OUT classes move away from this home page."}
        />
      )}
      <Modal
        open={Boolean(timeModal)}
        title={timeModal?.mode === "in" ? "Add IN Time" : "Edit IN / Add OUT"}
        description="Default time is current time. You can edit it manually."
        onClose={() => setTimeModal(null)}
      >
        <form className="form-grid" onSubmit={handleSaveTime}>
          <div className="selected-routine">
            <strong>Room {timeModal?.roomNumber || timeModal?.routine?.roomNumber}</strong>
            <span>
              {timeModal?.routine?.className} - {timeModal?.routine?.sectionName} · {timeModal?.routine?.subjectName}
            </span>
          </div>
          <div className="form-grid two">
            <Field label="Room number">
              <input
                value={timeModal?.roomNumber || ""}
                onChange={(event) => setTimeModal((value) => ({ ...value, roomNumber: event.target.value }))}
                placeholder="Room number"
              />
            </Field>
            <Field label="Teacher">
              <select
                value={timeModal?.teacherName || ""}
                onChange={(event) => setTimeModal((value) => ({ ...value, teacherName: event.target.value }))}
                required
              >
                <option value="">Select teacher</option>
                {(routineMasters?.teachers || []).map((teacher) => (
                  <option key={teacher.id} value={teacher.name}>{teacher.name}</option>
                ))}
              </select>
            </Field>
          </div>
          <Field label="IN Time">
            <input
              type="time"
              value={timeModal?.actualIn || ""}
              onChange={(event) => setTimeModal((value) => ({ ...value, actualIn: event.target.value }))}
              required
            />
          </Field>
          {timeModal?.mode === "in" ? (
            <label className="optional-out-toggle">
              <input
                type="checkbox"
                checked={Boolean(timeModal?.includeOut)}
                onChange={(event) => setTimeModal((value) => ({ ...value, includeOut: event.target.checked }))}
              />
              <span>Optional: add OUT time now</span>
            </label>
          ) : null}
          {timeModal?.mode === "out" || timeModal?.includeOut ? (
            <Field label="OUT Time">
              <input
                type="time"
                value={timeModal?.actualOut || ""}
                onChange={(event) => setTimeModal((value) => ({ ...value, actualOut: event.target.value }))}
                required
              />
            </Field>
          ) : null}
          <div className="form-actions">
            <button className="btn btn-ghost" type="button" onClick={() => setTimeModal(null)}>
              <X size={17} />
              Cancel
            </button>
            <button className="btn btn-primary" type="submit" disabled={saving}>
              {saving ? <span className="spinner" /> : <Save size={17} />}
              Save
            </button>
          </div>
        </form>
      </Modal>
    </>
  );
}
